import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SwipeToRevealRow.scss';

// Touch-only "swipe left to reveal an action" row (iOS Mail style). Wraps a
// list row; dragging left slides the content over and exposes `actions`
// (usually one destructive button) behind it. Only one row is open at a time
// (module-level closer). A tap on an open row closes it instead of firing the
// row's click — the row's own onClick is suppressed via onClickCapture while
// a swipe/open is in flight.
//
// `enabled=false` renders the children untouched (desktop keeps its hover
// affordances). Vertical scrolling is left to the browser: touch-action:
// pan-y on the content, and the axis is decided from the first ~8px of
// movement, so a slightly diagonal scroll never turns into a swipe.

let closeOpenRow = null;

const AXIS_THRESHOLD = 8;

const SwipeToRevealRow = ({
    enabled = true,
    actionWidth = 88,
    actions,
    className = '',
    children,
}) => {
    const [offset, setOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    // The action strip is only in the DOM while revealed (dragging / open /
    // the 200ms close animation). Painting it permanently under the row and
    // relying on the row's translated layer to cover it left red slivers on
    // iOS during scroll (late/subpixel repaint of the covering layer).
    const [revealed, setRevealed] = useState(false);
    const hideTimer = useRef(null);
    const reveal = useCallback(() => {
        if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
        setRevealed(true);
    }, []);
    const scheduleHide = useCallback(() => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => { hideTimer.current = null; setRevealed(false); }, 260);
    }, []);
    useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);
    const start = useRef(null);       // { x, y, offset }
    const axis = useRef(null);        // 'x' | 'y' | null
    const swiped = useRef(false);     // suppress the click after a horizontal move
    const offsetRef = useRef(0);
    offsetRef.current = offset;

    const close = useCallback(() => {
        setOffset(0);
        scheduleHide();
        if (closeOpenRow === close) closeOpenRow = null;
    }, [scheduleHide]);

    useEffect(() => () => { if (closeOpenRow === close) closeOpenRow = null; }, [close]);

    // Pointer events (touch only): one code path for iOS/Android WebViews and
    // desktop-emulated touch. `touch-action: pan-y` on the content lets the
    // browser own vertical scrolling — when it takes the gesture we get
    // pointercancel and simply snap back.
    const onPointerDown = useCallback((e) => {
        if (e.pointerType !== 'touch' || !e.isPrimary) return;
        start.current = { x: e.clientX, y: e.clientY, offset: offsetRef.current, id: e.pointerId };
        axis.current = null;
        swiped.current = false;
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!start.current || e.pointerId !== start.current.id) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        if (!axis.current) {
            if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return;
            axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            if (axis.current === 'x') {
                setDragging(true);
                reveal();
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
                if (closeOpenRow && closeOpenRow !== close) closeOpenRow();
            }
        }
        if (axis.current !== 'x') return;
        swiped.current = true;
        const next = Math.max(-actionWidth, Math.min(0, start.current.offset + dx));
        setOffset(next);
    }, [actionWidth, close, reveal]);

    const onPointerEnd = useCallback((e) => {
        if (!start.current || (e && e.pointerId !== start.current.id)) return;
        start.current = null;
        setDragging(false);
        if (axis.current !== 'x') return;
        if (offsetRef.current < -actionWidth / 2) {
            setOffset(-actionWidth);
            closeOpenRow = close;
        } else {
            close();
        }
    }, [actionWidth, close]);

    // Swallow the click that follows a swipe, and turn a tap on an open row
    // into "close".
    const onClickCapture = useCallback((e) => {
        if (swiped.current || offsetRef.current !== 0) {
            e.stopPropagation();
            e.preventDefault();
            swiped.current = false;
            close();
        }
    }, [close]);

    if (!enabled) return children;

    return (
        <div className={`swipe-row${className ? ` ${className}` : ''}${offset !== 0 ? ' is-open' : ''}`}>
            {revealed && (
                <div className="swipe-row-actions" style={{ width: actionWidth }}>
                    {typeof actions === 'function' ? actions({ close }) : actions}
                </div>
            )}
            <div
                className={`swipe-row-content${dragging ? ' is-dragging' : ''}`}
                style={{ transform: `translateX(${offset}px)` }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                onClickCapture={onClickCapture}
            >
                {children}
            </div>
        </div>
    );
};

export default SwipeToRevealRow;
